import {
  CommonCollapseButton,
  CommonDeleteButton,
  CommonEditButton,
  CommonHeading,
  CommonNewButton,
  CommonSearch,
  ConfirmDialog,
  customUseEffect,
  dateTimeFormat,
  Grid,
  MuiDataGrid,
  TabHeaderLayout,
  useMemo
} from "@common";
import { ViewPdf } from "@common/attachment";
import { CAMPAIGN_MANAGEMENT, CRM, GENERATE } from "@constants";
import {
  rowSelect as attachmentRowSelect,
  deleteAttachment,
  getAttachment
} from "@features";
import { useApplicationPermissions } from "@hooks";
import { CRUD_ATTACHMENT_VIEW } from "@routes";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPage from "./AddPage";
import UpdatePage from "./UpdatePage";

const AttachmentPage = ({
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forContract,
  forOrder,
  contractId: propContractId,
  orderId: propOrderId,
  title = "Attachments",
  allowedFileTypes,
  ...props
}) => {
  const dispatch = useDispatch();
  const [showAdd, setShowAdd] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [viewMore, setViewMore] = useState(true);

  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [activePdfUrl, setActivePdfUrl] = useState(null);
  const [activePdfName, setActivePdfName] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const gridRow = useSelector((state) => state?.attachment?.data) || [];

  // Entity Selected Data resolution from Redux state
  const campaignSelectedData = useSelector(
    (state) => state?.campaign?.selectedData,
  );
  const prospectSelectedData = useSelector(
    (state) => state?.prospect?.selectedData,
  );
  const inquirySelectedData = useSelector(
    (state) => state?.inquiry?.selectedData,
  );
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector(
    (state) => state?.opportunity?.selectedData,
  );
  const contractSelectedData = useSelector(
    (state) => state?.contract?.selectedData,
  );
  const orderSelectedData = useSelector(
    (state) => state?.order?.selectedData,
  );

  const campaignId = forCampaign
    ? campaignSelectedData?.id || props?.campaignId
    : props?.campaignId || null;
  const prospectId = forProspect
    ? prospectSelectedData?.id || props?.prospectId || props?.prospectId
    : props?.prospectId || props?.prospectId || null;
  const inquiryId = forInquiry
    ? inquirySelectedData?.id || props?.inquiryId
    : props?.inquiryId || null;
  const leadId = forLead
    ? leadSelectedData?.id || props?.leadId
    : props?.leadId || null;
  const opportunityId = forOpportunity
    ? opportunitySelectedData?.id || props?.opportunityId
    : props?.opportunityId || null;
  const contractId = forContract
    ? (Array.isArray(contractSelectedData) ? contractSelectedData[0]?.id : contractSelectedData?.id) || propContractId || props?.contractId
    : propContractId || props?.contractId || null;
  const orderId = forOrder
    ? (Array.isArray(orderSelectedData) ? orderSelectedData[0]?.id : orderSelectedData?.id) || propOrderId || props?.orderId
    : propOrderId || props?.orderId || null;

  const permissionsList = useApplicationPermissions(
    CRM,
    GENERATE,
    CAMPAIGN_MANAGEMENT,
  );
  const permissions = useMemo(
    () => ({
      create: permissionsList?.createAttachment,
      update: permissionsList?.updateAttachment,
      delete: permissionsList?.deleteAttachment,
      viewFile: permissionsList?.viewAttachment,
    }),
    [permissionsList],
  );


  const originalRows = gridRow || [];
  const [filteredRows, setFilteredRows] = useState(originalRows);
  const [openFile, setOpenFile] = useState(false);
  const [rowData, setRowData] = useState("");

  customUseEffect(() => {
    setFilteredRows(originalRows);
  }, [originalRows]);

  const getFunction = () => {
    if (campaignId) {
      dispatch(getAttachment({ campaignId }));
    } else if (prospectId) {
      dispatch(getAttachment({ prospectId }));
    } else if (inquiryId) {
      dispatch(getAttachment({ inquiryId }));
    } else if (leadId) {
      dispatch(getAttachment({ leadId }));
    } else if (opportunityId) {
      dispatch(getAttachment({ opportunityId }));
    } else if (contractId) {
      dispatch(getAttachment({ contractId }));
    } else if (orderId) {
      dispatch(getAttachment({ orderId }));
    } else {
      dispatch(getAttachment());
    }
  };
  customUseEffect(() => {
    setViewMore(true);
    getFunction();
    return () => dispatch(attachmentRowSelect([{}]));
  }, [campaignId, prospectId, inquiryId, leadId, opportunityId, contractId, orderId]);


  const handleSuccess = (res) => {
    if (res?.payload?.ok || res?.meta?.requestStatus === "fulfilled") {
      getFunction();
    }
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleDelete = (item) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure to delete this record?",
      subTitle: "You can't undo this operation.",
      onConfirm: () => {
        dispatch(deleteAttachment({ id: item.id })).then(handleSuccess);
      },
    });
  };

  const data = {
    rows: filteredRows || [],
    columns: [
      {
        field: "fileName",
        headerName: "File",
        renderHeader: () => <strong>File</strong>,
        width: 450,
        renderCell: (params) => (
          <button
            onClick={() => {
              if (!permissions?.viewFile) return;
              setOpenFile(true); setRowData(params?.row);
            }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit", color: "blue", textDecoration: "underline" }}
            disabled={!permissions?.viewFile}
          >
            {params.value}
          </button>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        renderHeader: () => <strong>Description</strong>,
        width: 300,
      },
      permissions?.update && {
        field: "Edit",
        type: "actions",
        headerName: "Delete",
        renderHeader: () => <strong>Edit</strong>,
        width: 40,
        renderCell: (value) => {
          const row = value?.row;
          return (
            <CommonEditButton
              handleClick={() => {
                setSelectedData(row);
                setShowUpdate(true);
              }}
              removePadding={true}
              permission={permissions?.update}
            />
          );
        },
      },
      permissions?.delete && {
        field: "Delete",
        type: "actions",
        headerName: "Delete",
        renderHeader: () => <strong>Delete</strong>,
        width: 80,
        renderCell: (value) => {
          const row = value?.row;
          return (
            <CommonDeleteButton
              handleClick={() => handleDelete(row)}
              removePadding={true}
              permission={permissions?.delete}
            />
          );
        },
      },
      {
        field: "createdOn",
        headerName: "Uploaded On",
        renderHeader: () => <strong>Uploaded On</strong>,
        valueGetter: (value, row) => row?.createdOn, // Keep raw date for sorting
        valueFormatter: (value, row) => dateTimeFormat(value), // Format for display
        sortComparator: (v1, v2) => new Date(v1) - new Date(v2), // Ensure correct sorting
        width: 250,
      },
      {
        field: "createdby",
        headerName: "Uploaded By",
        renderHeader: () => <strong>Uploaded By</strong>,
        valueGetter: (value, row) => row?.user_created?.email, // Keep raw date for sorting
        width: 250,
      },

    ],
  };

  const showAddFunction = () => setShowAdd(true);

  return (
    <div>
      <TabHeaderLayout>
        <Grid
          size={{ md: 2.5, lg: 2.5, xs: 2.5, xl: 2.5, sm: 2.5 }}
          style={{ display: "flex", justifyContent: "left" }}
        >
          <CommonCollapseButton viewMore={viewMore} setViewMore={setViewMore} />
          {permissions?.create && (
            <CommonNewButton handleNew={showAddFunction} />
          )}
        </Grid>
        <Grid
          size={{ md: 6, lg: 6, xs: 6, xl: 6, sm: 6 }}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <CommonHeading heading={title} />
        </Grid>
        <Grid
          size={{ md: 3.3, lg: 3.3, xs: 3.5, xl: 3.3, sm: 3.5 }}
          style={{ display: "flex", justifyContent: "right" }}
        >
          <CommonSearch
            rows={originalRows}
            searchFields={["fileName", "file", "description"]}
            onFilter={setFilteredRows}
          />
        </Grid>
      </TabHeaderLayout>

      {viewMore && (
        <MuiDataGrid
          data={data}
          select={(selectedRows) => {
            dispatch(attachmentRowSelect(selectedRows));
          }}
        />
      )}

      {showAdd && (
        <AddPage
          showAdd={showAdd}
          close={setShowAdd}
          getFunction={getFunction}
          forCampaign={forCampaign}
          forProspect={forProspect}
          forInquiry={forInquiry}
          forLead={forLead}
          forOpportunity={forOpportunity}
          forContract={forContract}
          forOrder={forOrder}
          campaignId={campaignId}
          prospectId={prospectId}
          inquiryId={inquiryId}
          leadId={leadId}
          opportunityId={opportunityId}
          contractId={contractId}
          orderId={orderId}
          allowedFileTypes={allowedFileTypes || props?.allowedFileTypes}
        />
      )}

      {showUpdate && (
        <UpdatePage
          showUpdate={showUpdate}
          close={() => {
            setSelectedData(null);
            setShowUpdate(false);
          }}
          getFunction={getFunction}
          selectedData={selectedData}
          forCampaign={forCampaign}
          forProspect={forProspect}
          forInquiry={forInquiry}
          forLead={forLead}
          forOpportunity={forOpportunity}
          forContract={forContract}
          forOrder={forOrder}
          campaignId={campaignId}
          prospectId={prospectId}
          inquiryId={inquiryId}
          leadId={leadId}
          opportunityId={opportunityId}
          contractId={contractId}
          orderId={orderId}
          allowedFileTypes={allowedFileTypes || props?.allowedFileTypes}
        />
      )}

      {openFile && (
        <ViewPdf
          fileName={rowData.customTestScriptName}
          showAttachment={openFile}
          setShowAttachment={setOpenFile}
          havingViewUrl={`${CRUD_ATTACHMENT_VIEW}/${rowData?.id}`}
          variableDownload={`${CRUD_ATTACHMENT_VIEW}/${rowData?.id}?forDownload=${true
            }`}
        />
      )}

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </div>
  );
};

export default AttachmentPage;
